import axios from "axios";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { v4 as uuid } from "uuid";
import SProductLookUp from "../SProductLookUp";
import AddProductApprovalColors from "./AddProductApprovalColors";
import SizeSetQtyModal from "./SizeSetQtyModal";
import { mergeSetSizes } from "./sizeSetEntry";
import { getColorsArrayByIds } from "utils/UtilityFuncs";

export default function SProductsInfo({
  user,
  isUpdateMode,
  selectedWO,
  productsList,
  setProductsList,
  formData,
  setFormData,
  productTypesList,
  fabrics,
  setUpdateId,
  setPreviewImage,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  // The looked-up product's size sets (Sales > Utility > Products, user
  // 2026-10-30), offered for quantities once the product is filled in.
  const [lookupSets, setLookupSets] = useState([]);

  const openProductSizeSets = async (product) => {
    const ids = (product?.sizeSets || []).map(String);
    if (!ids.length) {
      // No size sets: the product's own sizes go through the same modal
      // instead of all being added (user, 2026-11-02).
      const names = (product?.sizesWithNames || []).filter(Boolean);
      if (names.length) {
        setLookupSets([
          { id: "sizes", name: "Sizes", items: names.map((name, i) => ({ id: i, size: { name } })) },
        ]);
      }
      return;
    }
    try {
      const { data } = await API.post("/sales/size-sets", {}, { headers: authHeader() });
      const byId = new Map((data?.data || []).map((set) => [String(set.id), set]));
      // Empty sets are shown too, so the user sees why they have no sizes.
      const sets = ids.map((id) => byId.get(id)).filter(Boolean);
      setLookupSets(sets);
    } catch {
      // no sets: the product is filled in without them
    }
  };

  const applyLookupSets = (entries) => {
    setFormData((prev) => {
      const { sizes, totalQuantity, totalPrice } = mergeSetSizes(prev.productSizes, entries);
      return { ...prev, productSizes: JSON.stringify(sizes), totalQuantity, totalPrice };
    });
    setLookupSets([]);
  };

  const [showLockupForm, setShowLockupForm] = useState(false);
  const [showColorsModal, setShowColorsModal] = useState(false);
  const [approvalColorNames, setApprovalColorNames] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [showColorPickModal, setShowColorPickModal] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [imageDownloading, setImageDownloading] = useState(false);
  const [colorSearch, setColorSearch] = useState("");
  const [colorDisplayCount, setColorDisplayCount] = useState(10);

  const [productApprovalStatus, setProductApprovalStatus] = useState(null);

  /*   All Functions
   ********************************************* */

  // Get Index of selected Product
  const getSelectedProductIndex = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = productsList.findIndex(
      (item) => item.id === formData.id,
    );
    return currentIndex;
  };

  const addSizesFromProduct = (product) => {
    const productSizes = product.sizesWithNames || [];

    // Parse existing sizes from formData
    let existingSizes = [];
    try {
      const raw = formData?.productSizes;
      existingSizes = raw
        ? typeof raw === "string"
          ? JSON.parse(raw)
          : raw
        : [];
    } catch {
      existingSizes = [];
    }

    // Build a set of existing size names for fast lookup (case-insensitive)
    const existingNames = new Set(
      existingSizes.map((s) => (s.size || "").toLowerCase()),
    );

    // Find the max sortId among existing sizes
    const maxSortId = existingSizes.reduce(
      (max, s) => Math.max(max, s.sortId || 0),
      0,
    );

    // Only add sizes not already present, keeping product sort order
    let offset = 0;
    const missingSizes = productSizes
      .filter((sizeName) => !existingNames.has((sizeName || "").toLowerCase()))
      .map((sizeName) => ({
        size: sizeName,
        quantity: "",
        price: "",
        cost: 0,
        id: uuid(),
        sortId: maxSortId + ++offset,
      }));

    return JSON.stringify([...existingSizes, ...missingSizes]);
  };

  const handleSelectedProduct = async (
    product,
    variation = null,
    skipColorPick = false,
  ) => {
    setPendingProduct(product);
    setColorSearch("");
    setColorDisplayCount(10);
    setShowLockupForm(false);
    if (variation) {
      // Variation already chosen in lookup — skip the modal
      await handleConfirmColors(product, variation);
    } else if (skipColorPick) {
      // No variations available — apply product data without touching color/image
      await handleConfirmColors(product, null);
    } else {
      setSelectedVariation(null);
      setShowColorPickModal(true);
    }
  };

  const handleConfirmColors = async (
    overrideProduct = null,
    overrideVariation = null,
  ) => {
    const product = overrideProduct || pendingProduct;
    const variation = overrideVariation || selectedVariation;
    if (!product) return;
    // Sizes come only from the quantities modal, never every size of the
    // product (user, 2026-10-30 for size sets; 2026-11-02 without them).
    const _productSizes = addSizesFromProduct({ ...product, sizesWithNames: [] });

    let _productFabric = fabrics?.find(
      (fabric) => fabric.fabricId === product.fabricId,
    );
    _productFabric = _productFabric ? _productFabric.name : "";

    let parsedSizes = [];
    try {
      parsedSizes = JSON.parse(_productSizes);
    } catch {
      parsedSizes = [];
    }
    const totalQuantity = parsedSizes.reduce(
      (acc, s) => acc + parseInt(s.quantity || 0),
      0,
    );
    const totalPrice = parsedSizes.reduce(
      (acc, s) => acc + parseFloat(s.price || 0) * parseInt(s.quantity || 0),
      0,
    );

    // Parse color and teamName from variation.color (format: "color" or "color-teamName")
    const variationColor = variation?.color || "";
    const dashIdx = variationColor.indexOf("-");
    const colorPart =
      dashIdx !== -1 ? variationColor.slice(0, dashIdx) : variationColor;
    const teamPart = dashIdx !== -1 ? variationColor.slice(dashIdx + 1) : "";

    const _formData = {
      ...formData,
      productStyle: product.styleId,
      productDescription: product.desc,
      productType: product?.productType?.productTypes,
      productTypeId: product?.productType?.productTypeId,
      productFabric: _productFabric,
      productUrl: product.productUrl || "",
      isReady: product.isReady || false,
      productSizes: _productSizes,
      totalQuantity,
      totalPrice,
      ...(variation && {
        productColor: colorPart,
        teamName: teamPart || formData.teamName || "",
      }),
    };

    // If variation has images, download the first image and assign it as the product image file
    const variationImages =
      variation && Array.isArray(variation.images) ? variation.images : [];
    if (variationImages.length > 0) {
      try {
        setImageDownloading(true);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URI}/aws/images`,
          { key: variationImages[0] },
        );
        const signedUrl = response.data[0];
        const imgResponse = await fetch(signedUrl);
        const blob = await imgResponse.blob();
        const fileName =
          variationImages[0].split("/").pop() || "variation-image";
        const file = new File([blob], fileName, { type: blob.type });
        _formData.file = file;
        // Clear existing product image so the preview branch takes over
        _formData.productImage = null;
        // Generate data URL preview
        await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            if (setPreviewImage) setPreviewImage(reader.result);
            resolve();
          };
        });
      } catch (err) {
        dispatch(
          setNotification({
            message: "Could not download variation image: " + err.message,
            type: "error",
          }),
        );
      } finally {
        setImageDownloading(false);
      }
    }

    setFormData(_formData);
    setShowColorPickModal(false);
    setPendingProduct(null);
    setSelectedVariation(null);
    openProductSizeSets(product);
  };

  // Fetch colors by aggregated IDs for all products once
  const fetchColorsByIds = useCallback(
    async (colorIds) => {
      if (!colorIds || colorIds.length === 0) {
        return;
      }

      try {
        setLoading(true);
        const data = await getColorsArrayByIds(colorIds);

        if (data && data.length > 0) {
          const colors = data || [];

          setApprovalColorNames(colors.map((color) => color.name));
          setFormData((prev) => ({
            ...prev,
            productColor: "",
          }));
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to load colors",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, formData.approvalColors],
  );

  useEffect(() => {
    // Aggregate all color IDs from productsList
    let colorIdsSet = new Set();
    formData.approvalColors?.forEach((colorId) => {
      colorIdsSet.add(colorId);
    });
    // Fetch colors by aggregated IDs
    if (colorIdsSet.size === 0) {
      setApprovalColorNames(null);
      return;
    }
    const uniqueColorIds = Array.from(colorIdsSet);

    fetchColorsByIds(uniqueColorIds);
  }, [formData.approvalColors, fetchColorsByIds]);

  // const searchPackedBy = async (searchText, fieldName) => {
  //   try {
  //     setLoading(true);
  //     const { data } = await API.post(
  //       "/wo-products/autocomplete-search",
  //       {
  //         searchText,
  //         searchField: fieldName,
  //       },
  //       { headers: authHeader() }
  //     );
  //     if (data?.success) {
  //       setLoading(false);
  //       return data.data;
  //     }
  //     setLoading(false);
  //   } catch (err) {
  //     setLoading(false);
  //     dispatch(
  //       setNotification({
  //         message: err.message,
  //         type: "error",
  //       })
  //     );
  //   }
  // };

  /*   All UseEffects
   ********************************************* */

  return (
    <div>
      {loading && <Spinner />}

      <div className="d-flex justify-content-between align-items-center my-1 px-2">
        <h3>
          Product
          {` {${
            productsList?.length === 0
              ? " 0 "
              : ` ${getSelectedProductIndex() + 1} `
          } of ${productsList?.length} }`}
        </h3>

        <div className="d-flex gap-1">
          {formData && isUpdateMode && (
            <>
              <div className="d-flex align-items-center gap-1">
                {/* Approval Status */}
                <span
                  className={`btn btn-sm rounded-3 d-flex align-items-center justify-content-center pe-none ${
                    formData?.approveStatus === "approved"
                      ? "btn-success"
                      : formData?.approveStatus === "rejected"
                        ? "btn-danger"
                        : "btn-secondary"
                  }`}
                  style={{ width: "25px", height: "25px" }}
                  title={formData?.approveStatus || "pending"}
                >
                  A
                </span>
                {/* Purchased */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFormData((prev) => ({
                      ...prev,
                      purchased: !prev?.purchased,
                    }));
                  }}
                  className={`btn btn-sm rounded-3 d-flex align-items-center justify-content-center ${
                    formData?.purchased ? "btn-success" : "btn-secondary"
                  }`}
                  style={{ width: "25px", height: "25px" }}
                  aria-label="Toggle purchased"
                >
                  P
                </button>
                {/* Received */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFormData((prev) => ({
                      ...prev,
                      received: !prev?.received,
                    }));
                  }}
                  className={`btn btn-sm rounded-3 d-flex align-items-center justify-content-center ${
                    formData?.received ? "btn-success" : "btn-secondary"
                  }`}
                  style={{ width: "25px", height: "25px" }}
                  aria-label="Toggle received"
                >
                  R
                </button>
                {/* Ready */}
                <div className="d-flex align-items-center gap-2 ms-1">
                  <label>Ready?</label>
                  <div>
                    <input
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFormData((prev) => ({
                          ...prev,
                          isReady: e.target.checked,
                        }));
                      }}
                      checked={formData?.isReady || false}
                      type="checkbox"
                      id="switch2"
                      data-switch="success"
                    />
                    <label
                      htmlFor="switch2"
                      data-on-label="Yes"
                      data-off-label="No"
                    ></label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-2">
        <div className="row mb-2">
          <label className="col-4 text-end">Product Type</label>
          <div className="col-8">
            <select
              onChange={(e) => {
                e.preventDefault();
                // get selected product type
                const selectedProductType = productTypesList.find(
                  (type) => type.productTypeId === parseInt(e.target.value),
                );
                setFormData({
                  ...formData,
                  productType: selectedProductType?.productTypes,
                  productTypeId: selectedProductType
                    ? selectedProductType?.productTypeId
                    : "",
                });
              }}
              value={formData?.productTypeId || ""}
              className="form-select"
              id="commision_type"
              required
            >
              <option value={""}>Select Product Type</option>
              {productTypesList &&
                productTypesList.map((productType, i) => (
                  <option key={i} value={productType?.productTypeId}>
                    {productType?.productTypes}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="row mb-2">
          <label className="col-4 text-end">Product Style</label>
          <div className="col-8 d-flex align-items-center">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                required
                value={formData?.productStyle || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    productStyle: e.target.value,
                  })
                }
                placeholder="Enter product style"
              />
              <button
                onClick={() => {
                  setShowLockupForm(true);
                }}
                type="button"
                className="btn btn-sm btn-info"
              >
                L
              </button>
            </div>
          </div>
        </div>
        <div className="row mb-2">
          <label className="col-4 text-end">Product Description</label>
          <div className="col-8 ">
            <input
              type="text"
              value={formData?.productDescription || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  productDescription: e.target.value,
                })
              }
              className="form-control"
              placeholder="Product description"
            />
          </div>
        </div>
        <div className="row mb-2">
          <label className="col-4 text-end">Product Fabric</label>
          <div className="col-8 ">
            {/* products select */}
            <select
              onChange={(e) =>
                setFormData({
                  ...formData,
                  productFabric: e.target.value,
                })
              }
              value={formData?.productFabric || ""}
              className="form-select"
              id="product_fabric"
              required
            >
              <option value={""}>Select Product Fabric</option>
              {fabrics &&
                fabrics?.map((fabric, i) => (
                  <option key={i} value={fabric.name}>
                    {fabric.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="row mb-2">
          <label className="col-4 text-end">Product Color</label>
          <div className="col-8 d-flex align-items-center gap-0">
            <div className="input-group">
              <input
                type="text"
                disabled={approvalColorNames?.length > 0}
                value={
                  approvalColorNames
                    ? approvalColorNames.join(", ")
                    : formData?.productColor || ""
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    productColor: e.target.value,
                  })
                }
                className="form-control"
                placeholder="Product color"
              />
              <button
                onClick={(e) => {
                  setShowColorsModal(true);
                }}
                className={`btn ${
                  formData?.approvalColors &&
                  formData?.approvalColors.length > 0
                    ? "btn-success"
                    : "btn-secondary"
                }`}
              >
                C
              </button>
            </div>
          </div>
        </div>

        <div className="row mb-2">
          <label className="col-4 text-end">Team Name</label>
          <div className="col-8 ">
            <div className="input-group">
              <input
                type="text"
                value={formData?.teamName || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    teamName: e.target.value,
                  })
                }
                className="form-control"
                placeholder="Team Name"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Colors Crud for products */}
      <Modal
        show={showColorsModal}
        onHide={() => setShowColorsModal(false)}
        centered
        size=""
        // backdrop="static"
        // keyboard={false}
        contentClassName="bg-white custom-shadow-sm custom-border-radius"
      >
        <Modal.Body>
          <h3 className="">Product Colors</h3>
          <hr className="mt-0 mb-2" />
          <AddProductApprovalColors
            formData={formData}
            setFormData={setFormData}
          />
          <div className="text-end">
            <button
              onClick={() => setShowColorsModal(false)}
              type="button"
              className="btn btn-sm btn-secondary mt-2"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Select Colors from Product */}
      <Modal
        show={showColorPickModal}
        onHide={() => {
          setShowColorPickModal(false);
          setPendingProduct(null);
          setSelectedVariation(null);
          setColorSearch("");
          setColorDisplayCount(10);
        }}
        centered
        size="sm"
        contentClassName="bg-white custom-shadow-sm custom-border-radius"
      >
        <Modal.Body>
          <h3 className="">Select Color</h3>
          <hr className="mt-0 mb-2" />
          {pendingProduct &&
            (() => {
              let allVariations = [];
              if (Array.isArray(pendingProduct.variations)) {
                allVariations = pendingProduct.variations;
              } else if (typeof pendingProduct.variations === "string") {
                try {
                  const parsed = JSON.parse(pendingProduct.variations);
                  allVariations = Array.isArray(parsed) ? parsed : [];
                } catch {
                  allVariations = [];
                }
              }
              const selectedKey = selectedVariation
                ? selectedVariation.variationName || selectedVariation.color
                : "";
              return (
                <div>
                  {allVariations.length === 0 ? (
                    <p className="text-muted text-center">
                      No variations available for this product.
                    </p>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedKey}
                      onChange={(e) => {
                        const val = e.target.value;
                        const found = allVariations.find(
                          (v) => (v.variationName || v.color) === val,
                        );
                        setSelectedVariation(found || null);
                      }}
                    >
                      <option value="">-- Select a variation --</option>
                      {allVariations.map((variation) => {
                        const key = variation.variationName || variation.color;
                        const hasImage =
                          Array.isArray(variation.images) &&
                          variation.images.length > 0;
                        return (
                          <option key={key} value={key}>
                            {variation.color}
                            {hasImage ? " 🖼" : ""}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              );
            })()}
          {imageDownloading && (
            <div className="d-flex align-items-center gap-2 mt-3 text-primary">
              <div className="spinner-border spinner-border-sm" role="status" />
              <span>Downloading variation image...</span>
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowColorPickModal(false);
                setPendingProduct(null);
                setSelectedVariation(null);
                setColorSearch("");
                setColorDisplayCount(10);
              }}
              disabled={imageDownloading}
              type="button"
              className="btn btn-sm btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmColors}
              disabled={!selectedVariation || imageDownloading}
              type="button"
              className="btn btn-sm btn-primary"
            >
              {imageDownloading ? "Downloading..." : "Confirm"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* The looked-up product's size sets, for quantities */}
      <SizeSetQtyModal
        show={lookupSets.length > 0}
        sets={lookupSets}
        title={lookupSets[0]?.id === "sizes" ? "Size Quantities" : undefined}
        onApply={applyLookupSets}
        onHide={() => setLookupSets([])}
      />

      {/* Select Products */}
      <Modal
        show={showLockupForm}
        onHide={() => setShowLockupForm(false)}
        centered
        size="lg"
        // backdrop="static"
        // keyboard={false}
        contentClassName="bg-white custom-shadow-sm custom-border-radius"
      >
        <Modal.Body>
          <h3 className="">Select Product</h3>
          <hr className="mt-0 mb-3" />
          <SProductLookUp
            handleSelectedProduct={handleSelectedProduct}
            formData={formData}
            isShowModal={showLockupForm}
          />
          <div className="text-end">
            <button
              onClick={() => setShowLockupForm(false)}
              type="button"
              className="btn btn-sm btn-secondary mt-2"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
